<template>
  <div class="space-y-4">
    <!-- User Search and Selection -->
    <FormField name="participantSearch" v-if="canEdit">
      <FormItem>
        <Combobox v-model="comboboxModel" @update:model-value="handleUserSelection" by="id">
          <ComboboxAnchor as-child class="max-w-full w-[400px]">
            <ComboboxTrigger
              class="text-left border-gray-300 rounded-md border-[1px] w-[400px] hover:bg-gray-100 shadow-sm font-medium text-gray-800 flex items-center justify-between p-2 px-6 text-sm"
              :disabled="usersStore.isLoading || disabled || !canEdit"
            >
              <span>{{ comboboxModel ? `${comboboxModel.first_name} ${comboboxModel.last_name}` : 'Select user to add' }}</span>
              <ChevronsUpDown class="ml-2 h-4 w-4 opacity-50" />
            </ComboboxTrigger>
          </ComboboxAnchor>

          <ComboboxList class="max-w-full w-[400px] h-[20rem] flex flex-col" :is-loading="usersStore.isLoading">
            <!-- Input inside the list -->
            <div class="relative px-2 pt-2">
               <Search class="absolute left-4 top-4 h-4 w-4 text-muted-foreground" />
               <ComboboxInput 
                 placeholder="Search users..." 
                 v-model="userSearchQuery"
                 class="w-full h-9 pl-8" 
                 :disabled="disabled"
               />
             </div>

            <ComboboxEmpty>
              <div v-if="usersStore.isLoading" class="flex items-center justify-center p-4">
                <Loader2 class="h-4 w-4 animate-spin mr-2" />
                Loading users...
              </div>
              <div v-else>No users found.</div>
            </ComboboxEmpty>

            <ComboboxGroup class="overflow-y-auto flex-grow">
              <ComboboxItem
                v-for="user in availableUsersForSelect"
                :key="user.id"
                :value="user"
              >
                {{ user.first_name }} {{ user.last_name }} ({{ user.email }})
                 <ComboboxItemIndicator>
                   <Check class="ml-auto h-4 w-4" />
                 </ComboboxItemIndicator>
              </ComboboxItem>
            </ComboboxGroup>

            <ComboboxSeparator v-if="canLoadMoreUsers" />
             <div v-if="canLoadMoreUsers" class="p-1 h-10">
                <Button
                 variant="ghost"
                 class="w-full justify-center text-sm"
                 @click="loadMoreUsers"
                  :disabled="usersStore.isLoading || disabled"
               >
                 <Loader2 v-if="usersStore.isLoading" class="mr-2 h-4 w-4 animate-spin" />
                 {{ usersStore.isLoading ? 'Loading...' : 'Show More' }}
               </Button>
             </div>
          </ComboboxList>
        </Combobox>
        <FormMessage />
      </FormItem>
    </FormField>

    <!-- Selected Users List -->
    <div>
      <div class="flex items-center gap-2 mb-2" v-if="canEdit">
        <h3 class="text-md font-medium">
          Added Participants
        </h3>
      </div>
      <div
        v-if="modelValue.length > 0"
        class="space-y-2 overflow-y-auto max-h-[20rem] mt-4"
      >
        <div v-if="usersStore.isLoading" class="flex items-center justify-center p-4">
          <Loader2 class="h-4 w-4 animate-spin mr-2" />
          Loading participants...
        </div>
        <div
          v-for="user in modelValue"
          :key="user.id"
          class="flex items-center hover:bg-gray-100 rounded cursor-default h-7"
        >
          <img v-if="user.avatar_image" :src="user.avatar_image" class="w-5 h-5 rounded-full mr-2 object-cover" />
          <div v-else class="w-5 h-5 rounded-full mr-2 bg-gray-200 flex items-center justify-center">
            <UserRoundIcon class="w-3 h-3 text-gray-600" />
          </div>
          <span class="flex-grow text-sm">{{ user.first_name }} {{ user.last_name }}</span>
          <Button variant="ghost" size="xs" @click="removeUser(user.id)" :disabled="disabled" v-if="canEdit">
            <XIcon class="w-3 h-3" />
          </Button>
        </div>
      </div>
      <p v-else class="text-sm text-gray-500">No participants added yet.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, watchEffect } from 'vue'
import { useUsersStore } from '@/stores/users'
import { storeToRefs } from 'pinia'
import type { User } from '@/types/user'
import { Check, ChevronsUpDown, Search, UserRoundIcon, XIcon, Loader2 } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import { FormField, FormItem, FormMessage } from '@/components/ui/form'
import {
  Combobox,
  ComboboxAnchor,
  ComboboxTrigger,
  ComboboxItemIndicator,
  ComboboxInput,
  ComboboxList,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxItem,
  ComboboxSeparator
} from '@/components/ui/combobox'

import { useAuth } from '@/composables/useAuth'

const { isAdmin } = useAuth()

// Props & Emits
const props = withDefaults(
  defineProps<{
    modelValue: User[]
    disabled?: boolean
    canEdit?: boolean
  }>(),
  { disabled: false, canEdit: undefined }
)

const canEdit = computed(() => props.canEdit ?? isAdmin.value)

const emit = defineEmits(['update:modelValue'])

// Watch the modelValue prop for changes
watchEffect(() => {
  console.log('UserSelection: modelValue updated:', props.modelValue);
});

// Store
const usersStore = useUsersStore()
const { users: storeUsers, totalCount: storeTotalUsers, isLoading: storeIsLoading } = storeToRefs(usersStore)

const userSearchQuery = ref('')
const userPageSize = ref(20)
const comboboxModel = ref<User | null>(null) // Model for the Combobox
let searchDebounceTimer: number | null = null

const availableUsersForSelect = computed(() => {
  const selectedIds = new Set(props.modelValue.map(user => user.id));
  return storeUsers.value.filter((user) => !selectedIds.has(user.id))
})

const canLoadMoreUsers = computed(() => {
  return storeUsers.value.length < storeTotalUsers.value
})

const fetchUsers = (append = false) => {
  const currentPageSize = append && storeUsers.value.length > 0
      ? storeUsers.value.length + userPageSize.value
      : userPageSize.value;

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  usersStore.fetchUsers({
    page_size: currentPageSize,
    ordering: 'first_name',
    search: userSearchQuery.value || undefined
  })
}

watch(userSearchQuery, () => {
  if (searchDebounceTimer) clearTimeout(searchDebounceTimer)
  searchDebounceTimer = window.setTimeout(() => {
    fetchUsers()
  }, 500)
})

const loadMoreUsers = () => {
  if (props.disabled) {
    return
  }
  fetchUsers(true)
}

const handleUserSelection = (selectedItem: any) => {
  if (props.disabled) {
    return
  }
  if (
    selectedItem &&
    typeof selectedItem === 'object' &&
    selectedItem.id &&
    !props.modelValue.some(user => user.id === selectedItem.id)
  ) {
    const newSelectedUsers = [...props.modelValue, selectedItem]
    emit('update:modelValue', newSelectedUsers)
  }
  comboboxModel.value = null
  userSearchQuery.value = ''
}

const removeUser = (userId: number) => {
  if (props.disabled) {
    return
  }
  const newSelectedUsers = props.modelValue.filter((user) => user.id !== userId) 
  emit('update:modelValue', newSelectedUsers)
}

onMounted(() => {
  fetchUsers() // Fetch initial list of users
})

</script> 